import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Bell,
  ChevronDown,
  BarChart3,
  Users,
  Activity,
  User,
  Settings,
  Shield,
  LogOut,
  X,
  Calendar,
  FileText,
  AlertTriangle,
  Moon,
  Sun,
  Menu,
  FlaskConical,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import Breadcrumb from './Breadcrumb';
import './Layout.css';

const STUDIES = [
  { id: 'test', name: 'Test Study', patientCount: '22 Patients' },
  { id: 'uc-davis', name: 'UC Davis', patientCount: '155 Patients' },
  { id: 'kaiser', name: 'Kaiser Roseville', patientCount: '98 Patients' },
];

const NAV_ITEMS = [
  { to: '/', label: 'Overview', icon: BarChart3, match: (path) => path === '/' || path === '/overview' },
  { to: '/users', label: 'Patients', icon: Users, match: (path) => path === '/users' },
  { to: '/patient-overview', label: 'Patient Analysis', icon: Activity, match: (path) => path === '/patient-overview' },
  { to: '/appointments', label: 'Appointments', icon: Calendar, match: (path) => path === '/appointments' },
  { to: '/notes', label: 'Notes', icon: FileText, match: (path) => path === '/notes' },
  { to: '/alerts', label: 'Alerts', icon: AlertTriangle, match: (path) => path === '/alerts' },
];

const StudyInfo = ({ study, className = '' }) => (
  <div className={`study-info ${className}`.trim()}>
    <span className="study-name">{study.name}</span>
    <span className="patient-count">{study.patientCount}</span>
  </div>
);

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isStudyMenuOpen, setIsStudyMenuOpen] = useState(false);
  const [selectedStudyId, setSelectedStudyId] = useState(STUDIES[0].id);
  const userMenuRef = useRef(null);
  const searchModalRef = useRef(null);
  const studyMenuRef = useRef(null);
  const desktopStudyRef = useRef(null);

  const selectedStudy = STUDIES.find((study) => study.id === selectedStudyId) || STUDIES[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (searchModalRef.current && !searchModalRef.current.contains(event.target)) {
        setIsSearchModalOpen(false);
      }
      const clickedInsideStudySelector =
        (desktopStudyRef.current && desktopStudyRef.current.contains(event.target)) ||
        (studyMenuRef.current && studyMenuRef.current.contains(event.target));
      if (!clickedInsideStudySelector) {
        setIsStudyMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMobileNavOpen(false);
        setIsStudyMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileNavOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileNavOpen]);

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    setIsStudyMenuOpen(false);
  };

  const handleMenuItemClick = (path) => {
    setIsUserMenuOpen(false);
    if (path === '/logout') {
      navigate('/');
    } else {
      navigate(path);
    }
  };

  const handleSearchIconClick = () => {
    setIsSearchModalOpen(true);
  };

  const handleSearchModalClose = () => {
    setIsSearchModalOpen(false);
  };

  const handleMobileNavToggle = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
    setIsStudyMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const handleStudyMenuToggle = () => {
    setIsStudyMenuOpen(!isStudyMenuOpen);
    setIsUserMenuOpen(false);
  };

  const handleStudySelect = (studyId) => {
    setSelectedStudyId(studyId);
    setIsStudyMenuOpen(false);
  };

  const renderStudyOptions = () => (
    <div className="study-dropdown">
      {STUDIES.map((study) => (
        <button
          key={study.id}
          type="button"
          className={`study-dropdown-item ${study.id === selectedStudyId ? 'selected' : ''}`}
          onClick={() => handleStudySelect(study.id)}
        >
          <span className="study-name">{study.name}</span>
          <span className="patient-count">{study.patientCount}</span>
        </button>
      ))}
    </div>
  );

  const renderNavLink = (item, variant = 'desktop') => {
    const Icon = item.icon;
    const isActive = item.match(location.pathname);

    return (
      <Link
        key={item.to}
        to={item.to}
        className={`nav-item ${isActive ? 'active' : ''} nav-item--${variant}`}
        onClick={() => setIsMobileNavOpen(false)}
        title={item.label}
      >
        <Icon size={20} />
        <span className="nav-label">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="layout">
      <header className="app-header">
        <div className="header-left">
          <button
            type="button"
            className="nav-menu-button"
            onClick={handleMobileNavToggle}
            aria-label={isMobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileNavOpen}
          >
            {isMobileNavOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <div className="logo">
            <img src="/logo_backround.png" alt="HiDO" className="logo-image" />
          </div>

          <div className="study-selector study-selector--desktop" ref={desktopStudyRef}>
            <button
              type="button"
              className="study-selector-button"
              onClick={handleStudyMenuToggle}
              aria-label="Select study"
              aria-expanded={isStudyMenuOpen}
            >
              <StudyInfo study={selectedStudy} />
              <ChevronDown size={16} className={`dropdown-chevron ${isStudyMenuOpen ? 'open' : ''}`} />
            </button>
            {isStudyMenuOpen && (
              <div className="study-menu-dropdown">
                {renderStudyOptions()}
              </div>
            )}
          </div>

          <div className="study-menu" ref={studyMenuRef}>
            <button
              type="button"
              className="study-menu-button"
              onClick={handleStudyMenuToggle}
              aria-label="Select study"
              aria-expanded={isStudyMenuOpen}
            >
              <FlaskConical size={20} />
              <StudyInfo study={selectedStudy} className="study-menu-study-info" />
              <ChevronDown size={16} className={`dropdown-chevron ${isStudyMenuOpen ? 'open' : ''}`} />
            </button>
            {isStudyMenuOpen && (
              <div className="study-menu-dropdown">
                {renderStudyOptions()}
              </div>
            )}
          </div>

          <nav className="main-nav" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => renderNavLink(item, 'desktop'))}
          </nav>
        </div>

        <div className="header-right">
          <div className="search-container">
            <Search className="search-icon" size={20} />
            <input
              type="text"
              placeholder="Search for patients, caretakers, dates, etc."
              className="global-search"
            />
          </div>

          <button
            type="button"
            className="search-icon-button"
            onClick={handleSearchIconClick}
            aria-label="Search"
          >
            <Search size={20} />
          </button>

          <button
            type="button"
            className="theme-toggle"
            onClick={toggleTheme}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <Link to="/alerts" className="notification-bell" aria-label="View alerts">
            <Bell size={20} />
            <span className="notification-badge">1</span>
          </Link>

          <div className="user-avatar" ref={userMenuRef}>
            <div className="avatar-button" onClick={handleUserMenuToggle}>
              <img
                src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face&auto=format"
                alt="User"
                className="avatar-image"
              />
              <ChevronDown size={16} className={`avatar-chevron ${isUserMenuOpen ? 'open' : ''}`} />
            </div>

            {isUserMenuOpen && (
              <div className="user-menu">
                <div className="user-menu-header">
                  <img
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face&auto=format"
                    alt="User"
                    className="menu-avatar"
                  />
                  <div className="user-info">
                    <span className="user-name">Dr. Smith</span>
                    <span className="user-email">dr.smith@hido.com</span>
                  </div>
                </div>
                <div className="menu-divider"></div>
                <div className="menu-items">
                  <button className="menu-item" onClick={() => handleMenuItemClick('/my-account')}>
                    <User size={16} />
                    My Account
                  </button>
                  <button className="menu-item" onClick={() => handleMenuItemClick('/settings')}>
                    <Settings size={16} />
                    Settings
                  </button>
                  <button className="menu-item" onClick={() => handleMenuItemClick('/administration')}>
                    <Shield size={16} />
                    Administration
                  </button>
                  <div className="menu-divider"></div>
                  <button className="menu-item logout" onClick={() => handleMenuItemClick('/logout')}>
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile navigation drawer */}
      <div
        className={`mobile-nav-overlay ${isMobileNavOpen ? 'open' : ''}`}
        onClick={handleMobileNavToggle}
        aria-hidden={!isMobileNavOpen}
      >
        <nav
          className="mobile-nav-panel"
          onClick={(e) => e.stopPropagation()}
          aria-label="Mobile navigation"
        >
          <div className="mobile-nav-header">
            <h2 className="mobile-nav-title">Menu</h2>
            <button
              type="button"
              className="mobile-nav-close"
              onClick={handleMobileNavToggle}
              aria-label="Close navigation menu"
            >
              <X size={22} />
            </button>
          </div>

          <div className="mobile-nav-study">
            <span className="mobile-nav-study-label">Current study</span>
            <StudyInfo study={selectedStudy} />
          </div>

          <div className="mobile-nav-links">
            {NAV_ITEMS.map((item) => renderNavLink(item, 'mobile'))}
          </div>
        </nav>
      </div>

      <Breadcrumb />

      <main className="main-content">
        {children}
      </main>

      {isSearchModalOpen && (
        <div className="search-modal-overlay" onClick={handleSearchModalClose}>
          <div className="search-modal" ref={searchModalRef} onClick={(e) => e.stopPropagation()}>
            <div className="search-modal-input-container">
              <Search className="search-modal-icon" size={20} />
              <input
                type="text"
                placeholder="Search for patients, caretakers, dates, etc."
                className="search-modal-input"
                autoFocus
              />
              <button className="search-modal-close" onClick={handleSearchModalClose}>
                <X size={20} />
              </button>
            </div>
            <div className="search-modal-content">
              <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Start typing to search...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
